import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import slugify from 'slugify';

import {
  Header,
  Input,
  Textarea,
  Button,
  FileInput,
  LoaderComponent,
} from 'components/common';
import CreatePropertyImages from 'components/CreatePropertyImages';
import propertyService from 'services/propertyService';
import PropertyFeatures from 'components/PropertyFeatures';

import { useAccount, useProvider } from 'wagmi';
import { useContract } from '@thirdweb-dev/react';
import { ethers } from 'ethers';
import { axiosData } from 'services/axiosData';
import { useToast } from 'components/common/Toast/ToastProvider';

const nftCollectionAddress = process.env.REACT_APP_COLLECTION_ADDRESS;

const CreateProperty = () => {
  const { address } = useAccount();
  const history = useNavigate();

  // Connect to contract using the address
  const { contract } = useContract(nftCollectionAddress);
  const toast = useToast();

  useEffect(() => {
    if (!address) {
      toast?.error('Please connect your wallet', 2500);
      history('/');
    }
  }, [address]);

  const CreatePropertySchema = Yup.object().shape({
    title: Yup.string()
      .min(5, 'Too Short!')
      .max(50, 'Too Long!')
      .required('Required'),
    type: Yup.string().required('Required'),
    address: Yup.string().required('Required'),
    owner: Yup.string().required('Required'),
    description: Yup.string().required('Required'),
    deedNo: Yup.string().required('Required'),
    deedYear: Yup.string().required('Required'),
    landCertificate: Yup.string().required('Required'),
    validId: Yup.string().required('Required'),
    proofOfPurchase: Yup.string().required('Required'),
  });

  const [imgs, setImages] = useState<any[]>([]);
  const [imgError, setImgError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [features, setFeatures] = useState<object>({});

  const [newProperty, setNewProperty] = useState<any>({});
  const handleCreatePptSubmit = async (values: any) => {
    if (imgs.length <= 1) {
      setImgError('Please upload at least two pictures');
    } else {
      values.primaryImage = imgs[0].url;
      values.otherImages = imgs.map(({ url }) => url);
      values.minted = false;
      values.features = features;
      values.contractAddress = nftCollectionAddress;
      setLoading(true);
      values.price = 0;
      try {
        const response = await propertyService.createProperty(values);
        if (response) {
          setNewProperty(response);
          const mintResponse = await propertyService.mintProperty({
            propertyId: response.propertyId,
            address,
          });

          if (mintResponse) {
            console.log(mintResponse);

            const tx = await contract?.erc721.signature.mint(mintResponse);
            console.log(tx?.receipt, tx);

            const tId = tx?.id;

            const tokenId = ethers.BigNumber.from(tId).toNumber();

            const res = await propertyService.addTokenIdToMintedProperty({
              propertyId: response.propertyId,
              tokenId,
            });

            if (res) {
              toast?.success('Property created and tokenized successfully');
              setLoading(false);
            }
          }
          setLoading(false);
        }
      } catch (error: any) {
        console.log(error);
        setLoading(false);
        alert(JSON.stringify(error));
      }
    }
  };
  const [isTitleTaken, setIsTitleTaken] = useState(false);
  const CheckIfSlugIsAvailable = async (title: string) => {
    if (title.trim().length < 5) {
      return;
    }

    const slug = slugify(title.trim(), { lower: true });

    axiosData
      .get(`/property/${slug}`)
      .then(async (res: any) => {
        if (res) {
          setLoading(false);
          setIsTitleTaken(true);
        }
      })
      .catch(() => {
        setLoading(false);
        setIsTitleTaken(false);
      })
      .then(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <div className="bg-black pb-16">
        <Header />
        {loading && <LoaderComponent />}

        <div className="container mx-auto px-4 md:px-8 xl:px-16 mt-16 xl:mt-32">
          <Formik
            initialValues={{
              title: '',
              type: 'apartment',
              owner: address,
              address: 'Nigeria',
              // price: '',
              // size: '',
              externalLink: '',
              properties: '',
              description: 'Soft',
              // collection: '',
              deedNo: '1',
              deedYear: '2022',
              landCertificate: '',
              validId: '',
              proofOfPurchase: '',
            }}
            validationSchema={CreatePropertySchema}
            onSubmit={(values) => {
              if (!isTitleTaken) {
                handleCreatePptSubmit(values);
              } else {
                alert('Title has already been used');
              }
            }}
          >
            {(props) => {
              const { values, errors, setFieldValue } = props || {};

              return (
                <Form className="">
                  <div className="mx-auto xl:px-32 md:px-16 sm:px-0">
                    <p className="text-white text-4xl font-medium">
                      Create a new property
                    </p>
                    <CreatePropertyImages
                      imgs={imgs}
                      setImages={setImages}
                      imgError={imgError}
                      setImgError={setImgError}
                    />
                  </div>
                  <div className="grid lg:grid-cols-2 sm:grid-cols-1 gap-14 mt-16">
                    <Input
                      label="Title"
                      placeholder="Property title"
                      className="bg-transparent border border-primary-2 py-2 px-3 mt-4 focus:outline-none text-white"
                      type="text"
                      name="title"
                      value={values?.title}
                      error={
                        errors?.title ||
                        (isTitleTaken ? 'Username already taken' : '')
                      }
                      onChange={(e: any) => {
                        const title = e.target.value;

                        setFieldValue('title', title);
                        // CheckIfSlugIsAvailable(title); //
                      }}
                      disabled={loading}
                      {...props}
                    />

                    <Input
                      label="Property type"
                      placeholder="Property type (land, house, building)"
                      className="bg-transparent border border-primary-2 py-2 mt-4 px-3 focus:outline-none text-white"
                      type="text"
                      name="type"
                      disabled={loading}
                      value={values?.type}
                      error={errors?.type}
                      {...props}
                    />

                    <Input
                      label="Owner"
                      placeholder="Owner of the property"
                      className="bg-transparent border border-primary-2 py-2 mt-4 px-3 focus:outline-none text-white"
                      type="text"
                      name="owner"
                      disabled={loading}
                      value={values?.owner}
                      error={errors?.owner}
                      {...props}
                    />

                    <Input
                      label="Address"
                      placeholder="Address of the property"
                      className="bg-transparent border border-primary-2 py-2 mt-4 px-3 focus:outline-none text-white"
                      type="text"
                      name="address"
                      disabled={loading}
                      value={values?.address}
                      error={errors?.address}
                      {...props}
                    />

                    {/* <Input
                      label="Price"
                      placeholder="Price of the property"
                      className="bg-transparent border border-primary-2 py-2 mt-4 px-3 focus:outline-none text-white"
                      type="text"
                      name="price"
                      disabled={loading}
                      value={values?.price}
                      error={errors?.price}
                      {...props}
                    /> */}

                    {/* <Input
                      label="Size"
                      placeholder="Sq/m2"
                      className="bg-transparent border border-primary-2 py-2 mt-4 px-3 focus:outline-none text-white"
                      type="text"
                      name="size"
                      disabled={loading}
                      value={values?.size}
                      error={errors?.size}
                      {...props}
                    /> */}

                    <Input
                      label="Deed No."
                      placeholder="Title deed no"
                      className="bg-transparent border border-primary-2 py-2 px-3 mt-4 focus:outline-none text-white"
                      type="text"
                      name="deedNo"
                      disabled={loading}
                      value={values?.deedNo}
                      error={errors?.deedNo}
                      {...props}
                    />

                    <Input
                      label="Deed Year"
                      placeholder="Deed issue year"
                      className="bg-transparent border border-primary-2 py-2 px-3 mt-4 focus:outline-none text-white"
                      type="text"
                      name="deedYear"
                      disabled={loading}
                      value={values?.deedYear}
                      error={errors?.deedYear}
                      {...props}
                    />

                    <Input
                      label="External link"
                      placeholder="https//xyxsite.come/buildings/#5412"
                      className="bg-transparent border border-primary-2 py-2 px-3 focus:outline-none text-white"
                      type="text"
                      name="externalLink"
                      headText="Sepatgon will include a link to this URL on the property’s detail page, so that users can click to learn more about it. You are welcome to link to your own webpage with more details."
                      disabled={loading}
                      value={values?.externalLink}
                      error={errors?.externalLink}
                      {...props}
                    />

                    <PropertyFeatures
                      label="Properties"
                      placeholder="Enter key"
                      className="bg-transparent border border-primary-2 py-2 px-3 focus:outline-none text-white"
                      type="text"
                      name="properties"
                      headText="These are features that are specific to your asset. ( rooms, bath, style, color etc )"
                      disabled={loading}
                      value={values?.properties}
                      error={errors?.properties}
                      setFieldValue={setFeatures}
                      // {...props}
                    />

                    <Textarea
                      label="Description"
                      className="bg-transparent border border-primary-2 py-2 px-3 focus:outline-none text-white"
                      type="text"
                      name="description"
                      cols="8"
                      rows="6"
                      max="300"
                      placeholder="Give a detailed description of your properties"
                      headText="This description will be included in property details."
                      disabled={loading}
                      value={values?.description}
                      error={errors?.description}
                      {...props}
                    />

                    <FileInput
                      label="Title Deed  / Land certifcate"
                      headText="Supported file type : PNG, JPEG, PDF. Max size : 10 MB  "
                      name="landCertificate"
                      value={values?.landCertificate}
                      error={errors?.landCertificate}
                      {...props}
                    />

                    <FileInput
                      label="Valid ID ( Passport, National ID Card, Driver’s Licsense)"
                      headText="Supported file type : PNG, JPEG, PDF. Max size : 10 MB"
                      name="validId"
                      value={values?.validId}
                      error={errors?.validId}
                      {...props}
                    />

                    <FileInput
                      label="Receipt / Proof of purchase"
                      headText="Supported file type : PNG, JPEG, PDF. Max size : 10 MB"
                      name="proofOfPurchase"
                      value={values?.proofOfPurchase}
                      error={errors?.proofOfPurchase}
                      {...props}
                    />

                    {/* <Select
                      label="Collection"
                      placeholder="Select collection"
                      className="bg-transparent border  mt-4 border-primary-2 py-2 px-3 focus:outline-none text-white"
                      type="select"
                      name="collection"
                      value={values?.collection}
                    >
                      <option value="collection-1">Collection 1</option>
                      <option value="collection-2">Collection 2</option>
                    </Select> */}
                  </div>

                  {/* <Link
                  to="/property/create/verify"
                  className="flex justify-center mt-16"
                > */}
                  <div className="flex justify-center">
                    <Button
                      className="px-12 py-3 mt-6"
                      type="submit"
                      variant="primary"
                    >
                      {loading ? '...' : 'Create Property'}
                    </Button>
                  </div>
                  {/* </Link> */}
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default CreateProperty;
